import type { ReactNode } from "react";
import { BAND_CLASSES, bandOf } from "@/shared/components/pulse/score-band";
import { cn } from "@/shared/lib/cn";

/// Big numeric score with a band-colored dot and optional `/10` suffix.
/// Pass `insufficient` to render the "—" sentinel instead of a number.
export function Score({
  value,
  size = "md",
  insufficient,
  hideSuffix,
  className,
}: {
  value: number | null | undefined;
  size?: "sm" | "md" | "lg" | "xl";
  insufficient?: boolean;
  hideSuffix?: boolean;
  className?: string;
}): ReactNode {
  const band = insufficient ? "empty" : bandOf(value);
  const tone = BAND_CLASSES[band];
  const isEmpty = insufficient || value == null;
  const display = isEmpty ? "—" : value.toFixed(1);
  const sizeMap = {
    sm: { num: "text-sm", dot: "size-1.5", suf: "text-3xs" },
    md: { num: "text-[22px]", dot: "size-2", suf: "text-xs" },
    lg: { num: "text-[34px]", dot: "size-2.5", suf: "text-sm" },
    xl: { num: "text-[42px]", dot: "size-3.5", suf: "text-base" },
  }[size];
  return (
    <span className={cn("inline-flex items-baseline gap-2", className)}>
      <span className="inline-flex items-center gap-2">
        <span
          className={cn("inline-block rounded-full", sizeMap.dot, tone.dot)}
          aria-hidden="true"
        />
        <span
          className={cn(
            "font-medium leading-none tracking-tight tabular-nums",
            sizeMap.num,
            isEmpty ? "text-text-tertiary" : "text-text-primary",
          )}
        >
          {display}
        </span>
      </span>
      {!hideSuffix ? (
        <span className={cn("font-mono text-text-quaternary", sizeMap.suf)}>/10</span>
      ) : null}
    </span>
  );
}

/// Compact pill, for table rows, dim breakdowns, anywhere the score is
/// inline rather than headline. Optional `label` appears next to the
/// number (e.g. "5.4 ↓" or "5.4 Workload").
export function ScoreChip({
  value,
  insufficient,
  label,
  className,
}: {
  value?: number | null;
  insufficient?: boolean;
  label?: string;
  className?: string;
}): ReactNode {
  const band = insufficient ? "empty" : bandOf(value);
  const tone = BAND_CLASSES[band];
  const isEmpty = insufficient || value == null;
  return (
    <span
      className={cn(
        "inline-flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-xs font-medium leading-none tabular-nums ring-1 ring-inset",
        tone.bg,
        tone.fg,
        tone.ring,
        className,
      )}
    >
      <span className={cn("inline-block size-1.5 rounded-full", tone.dot)} aria-hidden="true" />
      {isEmpty ? "—" : value.toFixed(1)}
      {label ? <span className="ml-0.5 font-normal opacity-70">{label}</span> : null}
    </span>
  );
}
